import { useCallback, useContext, useEffect, useMemo } from 'react';
import { ProductTableContext } from '../../../contexts/product-table-context';
import { type SaleStatDays, saleStatDays } from '../../../data/product';
import type { Filters } from '../../../hooks/products';
import { Select } from '../../forms';
import { withFilterField } from './filter-component';
import { FilterWrapper } from './filter-wrapper';
import { toSoldInOptions } from './sold-in-filter';

export const NotSoldInFilter = withFilterField(
  'notSoldIn',
  function NotSoldInFilter({ overrideSoldInDays }: { overrideSoldInDays?: SaleStatDays[] }) {
    const { filters, setFilters } = useContext(ProductTableContext);
    const { notSoldIn, soldIn } = filters;

    const soldInDays = useMemo(() => {
      const options = overrideSoldInDays || [...saleStatDays];

      // when soldIn is set, ensure notSoldIn is less
      return options
        .filter((option) => soldIn === undefined || option < soldIn)
        .sort((a, b) => b - a);
    }, [overrideSoldInDays, soldIn]);

    // if the user changes the soldIn filter so that the current notSoldIn filter is no longer available,
    // reset the notSoldIn filter to the first available option
    useEffect(() => {
      if (notSoldIn && !soldInDays.includes(notSoldIn)) {
        setFilters((prev: Filters) => ({ ...prev, notSoldIn: soldInDays[0] }));
      }
    }, [soldInDays, notSoldIn, setFilters]);

    const onChange = useCallback(
      (next: SaleStatDays) => {
        setFilters((prev: Filters) => ({ ...prev, notSoldIn: next }));
      },
      [setFilters]
    );

    const options = useMemo(() => toSoldInOptions(soldInDays), [soldInDays]);

    return (
      <FilterWrapper label="Not Sold In Last">
        <Select<SaleStatDays>
          value={notSoldIn ?? soldInDays[0]}
          onChange={onChange}
          options={options}
        />
      </FilterWrapper>
    );
  }
);
